import * as React from 'react';
import { type Message } from '@krishanmadan/core/src/types';
import { ASSISTANT_NAME } from '@krishanmadan/core/src/model/const';
import { LottieTypingMessage } from '../Lottie';
import { cssBody1, cssBody2 } from '../css';
import { styled } from '../../theme/theme';
import { MessageTypeMarkdown } from '../MessageTypes/MessageTypeMarkdown';
import { ChatConst } from '../chat/ChatConst';
import { useGlobalStore } from '../../store/useGlobalStore';

type Props = {
  message: Message;
  left: boolean;
  className?: string;
};

export const ChatViewMessageBox = ({
  message,
  left,
  className,
}: Props) => {
  const visitorName = useGlobalStore((store) => store.settings.visitorName);
  return (
    <Root className={className}>
      <Content left={left}>
        <ArrowContainer left={left}>
          {left ? <ArrowLeft /> : <ArrowRight />}
        </ArrowContainer>
        <MessageName>
          {message.role === 'assistant' ? ASSISTANT_NAME : visitorName}
        </MessageName>
        <MessageTextValue>
          {message.role === 'assistant' && message.content.length <= 0
            ? (
              <MessageContentTyping>
                <StyledLottieTypingMessage />
              </MessageContentTyping>
            )
            : (
              <MessageTypeMarkdown
                content={message.content}
              />
            )}
        </MessageTextValue>
      </Content>
    </Root>
  );
};

export const Root = styled.div`
  display: flex;
`;

type TContentProps = {
  left: boolean;
};

const Content = styled.div<TContentProps>`
  ${ChatConst.messageMediaParams};
  background: ${({
  theme,
  left,
}: any) => (left ? theme.legacy.colors.chatHeadLeft : theme.legacy.colors.chatHeadRight)};
  border-radius: 5px;
  display: flex;
  flex-direction: column;
  height: auto;
  padding: 12px 24px;
  position: relative;
`;

const MessageContentTyping = styled.div`
  background: transparent;
  min-width: 200px;
  margin-top: 12px;
`;

const ArrowContainer = styled.div<TContentProps>`
  position: absolute;
  ${({ left }) => (left ? 'right: -10px' : 'left: -10px')};
`;

const ArrowBase = styled.div`
  margin: 4px 0;
`;

const ArrowLeft = styled(ArrowBase)`
  :after {
    border-bottom: 10px solid transparent;
    border-left: 10px solid ${({ theme }: any) => theme.legacy.colors.chatHeadLeft};
    border-top: 10px solid transparent;
    content: '';
    height: 0;
    margin: -10px -10px;
    position: absolute;
    width: 0;
  }
`;

const ArrowRight = styled(ArrowBase)`
  :after {
    border-bottom: 10px solid transparent;
    border-right: 10px solid ${({ theme }: any) => theme.legacy.colors.chatHeadRight};
    border-top: 10px solid transparent;
    content: '';
    height: 0;
    margin: -10px 0;
    position: absolute;
    width: 0;
  }
`;

const MessageText = styled.div`
  ${ChatConst.messageContentMediaParams};
  color: ${({ theme }: any) => theme.legacy.colors.primaryText};
  float: left;
  overflow-wrap: break-word;
  text-align: left;
  word-wrap: break-word;
`;

const MessageName = styled(MessageText)`
  ${cssBody1};
  font-weight: 600;
`;

const MessageTextValue = styled(MessageText)`
  ${cssBody2};
`;

const height = 80;
const StyledLottieTypingMessage = styled(LottieTypingMessage)`
  height: ${height}px;
  margin-bottom: -24px;
  margin-left: -24px;
  margin-top: -24px;
  max-height: ${height}px;
  min-height: ${height}px;
  overflow: hidden;
  width: 93px;
`;
